import { cn } from '@ifixit/ui/lib/utils';
import { SvgAsset, type SvgAssetProps } from './svg-asset';
import type { ComponentProps } from 'react';

/**
 * Specialized version of SvgAsset specifically for icons.
 *
 * This component adds semantic meaning and appropriate default styling for icons.
 *
 * @param name - The ID of the SVG symbol within the file
 * @param src - Path to the SVG file, can be absolute or relative
 *
 * @example
 * // Basic usage with individual SVG file
 * <Icon name="arrow-right" src="/icons/arrow-right.svg" />
 *
 * // With custom styling
 * <Icon name="check" src="/icons/check.svg" className="size-6 text-brand-500" />
 *
 * @remarks
 * Uses the same SVG format requirements as SvgAsset.
 */
export function Icon({ className, ...other }: SvgAssetProps) {
   return <SvgAsset data-slot="icon" className={cn('fill-current', className)} {...other} />;
}

export type FaIconProps = ComponentProps<'svg'> & {
   name: string;
   family?: 'classic' | 'duotone';
   style?: 'solid' | 'regular' | 'brands';
   /**
    * Override the base URL used to resolve this icon's SVG. Defaults to the
    * value set via {@link configureFaIconBaseUrl}.
    */
   baseUrl?: string;
};

const DEFAULT_FA_ICON_BASE_URL = '/_next/charge/icons/v7';

let faIconBaseUrl = DEFAULT_FA_ICON_BASE_URL;

/**
 * Configure the base URL `FaIcon` uses to resolve Font Awesome SVGs.
 *
 * iFixit apps serve these icons from the Charge CDN, proxied at the default
 * path (`/_next/charge/icons/v7`). External host apps that don't have that
 * rewrite will 404 on the default path and render blank icons — they should
 * call this once at startup to point `FaIcon` at their own copy of the icons
 * (or at the CDN directly). Individual icons can still override per-instance
 * via the `baseUrl` prop.
 *
 * Intentionally a module-level setting (not React context) so `FaIcon` stays
 * usable from React Server Components.
 */
export function configureFaIconBaseUrl(baseUrl: string) {
   faIconBaseUrl = baseUrl;
}

/**
 * A specialized version of the Icon component for Font Awesome icons.
 *
 * This component automatically constructs the path to individual SVG files
 * based on the Font Awesome icon family, style, and name conventions.
 *
 * @param name - The name of the Font Awesome icon (without the 'fa-' prefix)
 * @param family - The Font Awesome icon family ('classic' or 'duotone', defaults to 'classic')
 * @param style - The Font Awesome icon style ('solid', 'regular', or 'brands', defaults to 'solid')
 *
 * @example
 * // Basic usage with defaults (classic, solid)
 * <FaIcon name="user" />
 *
 * // Using regular style
 * <FaIcon name="bell" style="regular" />
 *
 * // Using duotone family
 * <FaIcon name="star" family="duotone" />
 *
 * // With custom styling
 * <FaIcon name="check" className="size-5 text-blue-500" />
 *
 * @remarks
 * Font Awesome icons are hosted on the iFixit Charge CDN under the `static/icons/v7/`
 * directory, following the convention of `<family>/<style>/<name>.svg`.
 *
 * Since browsers don't allow the <use> tag to load icons directly from remote URLs due to
 * security restrictions, we proxy these icons through our Next.js app using the path prefix
 * `/_next/icons/`.
 *
 * Each individual SVG file should follow the same format requirements as described in the SvgAsset component,
 * with a single <symbol> tag containing the icon and proper viewBox attribute.
 *
 * To build and upload the icons with the prepare-svg.mjs script, follow scripts/README.md in this package.
 */
export function FaIcon({
   name,
   family = 'classic',
   style = 'solid',
   baseUrl,
   className,
}: FaIconProps) {
   const base = (baseUrl ?? faIconBaseUrl).replace(/\/+$/, '');
   // The version search params is used to invalidate cache when we make changes to existing icons
   const href = `${base}/${family}/${style}/${name}.svg?v=3`;
   return <Icon name={name} src={href} className={className} overflow="visible" />;
}
